import type { CSSProperties, ReactNode } from 'react'
import { useLocation, useMatches } from '@tanstack/react-router'
import { PanelLeftIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { adminPageMeta } from '@/features/shared/lib/admin-nav'
import type { AdminSessionUser } from './admin-account-menu'
import { AdminSidebar } from './admin-sidebar'

type AdminShellProps = {
  children: ReactNode
  isAdmin: boolean
  user: AdminSessionUser
}

/** Fallback title for routes outside ADMIN_NAV — path lookup wins. */
function pageTitleFromMatches(
  matches: ReturnType<typeof useMatches>,
): string | undefined {
  const raw = [...matches]
    .reverse()
    .map((m) => (m.staticData as { title?: string } | undefined)?.title)
    .find(Boolean)
  return raw
}

function MobileHeader({ title }: { title: string }) {
  const { isMobile } = useSidebar()
  if (!isMobile) return null
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/95 px-3 backdrop-blur md:hidden">
      <SidebarTrigger
        render={<Button type="button" variant="outline" size="icon-sm" />}
      >
        <PanelLeftIcon className="size-4" />
        <span className="sr-only">Open navigation</span>
      </SidebarTrigger>
      <p className="truncate text-sm font-semibold">{title}</p>
    </header>
  )
}

/**
 * Ops shell — SidebarProvider + page chrome (#772).
 * Content uses max-w-7xl and min-w-0 to avoid horizontal overflow.
 */
export function AdminShell({ children, isAdmin, user }: AdminShellProps) {
  const matches = useMatches()
  const { pathname } = useLocation()
  const { title, description } = adminPageMeta(
    pathname,
    pageTitleFromMatches(matches),
  )

  return (
    <SidebarProvider
      defaultOpen
      className="relative min-h-svh w-full overflow-x-hidden bg-background"
      style={
        {
          '--sidebar-width': '15rem',
          '--sidebar-width-icon': '3.25rem',
        } as CSSProperties
      }
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-50 h-1 bg-gradient-to-r from-primary via-signal to-primary/70"
        aria-hidden
      />

      <AdminSidebar isAdmin={isAdmin} user={user} />

      <SidebarInset className="flex min-h-svh min-w-0 flex-1 flex-col overflow-x-hidden">
        <a
          href="#admin-main"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:shadow-md focus:ring-2 focus:ring-ring"
        >
          Skip to content
        </a>
        <MobileHeader title={title} />

        <div className="mx-auto flex w-full max-w-7xl min-w-0 flex-1 flex-col gap-4 px-4 py-5 sm:px-6 lg:px-8 lg:pb-6">
          <header className="flex min-w-0 shrink-0 flex-col gap-1">
            <h1 className="text-pretty font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {title}
            </h1>
            <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          </header>
          {/* flex-1 min-h-0 so ops work surfaces can fill remaining viewport */}
          <main
            id="admin-main"
            className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 pb-8 lg:pb-0"
          >
            {children}
          </main>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
